import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { getCookie, setCookie } from "cookies-next";

import CustomPage from "../components/CustomPage/CustomPage";
import ModalCloseButton from "../shared/components/Modal/ModalCloseButton";
import { useStores } from "./StoreContext";

export const AppContext = createContext({});

export const useAppState = () => useContext(AppContext);

export default function ApplicationStateProvider(props) {
  const { children, isMobile, slugsByPageTypeMap, asPath } = props;
  const { modalStore, popupStore } = useStores();
  const { openModal, closeModal } = modalStore;
  const { allPopups } = popupStore;

  const findSlugByPageType = (pageType) => {
    const pageTypeKey = Object.keys(slugsByPageTypeMap).find(
      (key) => key === pageType
    );

    return slugsByPageTypeMap[pageTypeKey] || "/";
  };

  useEffect(() => {
    if (closeModal) closeModal();
    const path = asPath === "/" ? "homepage" : asPath.split("/")[1];
    let popupTimeout;

    const popupsToOpen = allPopups.filter((popup) => {
      return popup.showOn?.includes("everywhere") || popup.showOn?.includes(path);
    });

    const handleCloseModal = (popup) => {
      setCookie(`${popup.id}-wasPopupSeen`, true, {
        maxAge: popup.cookieExpirationDays * 60 * 60 * 24,
      });
      if (closeModal) closeModal();
    };

    const showPopup = (popup) => {
      openModal({
        content: (
          <div className="p-5 popup-modal">
            <div className="d-flex justify-content-end py-3">
              <ModalCloseButton onClick={() => handleCloseModal(popup)} />
            </div>
            <CustomPage pageAttributes={popup} />
          </div>
        ),
      });
    };
    const listeners = popupsToOpen.map((popup) => {
      const handleListener = () =>
        !getCookie(`${popup.id}-wasPopupSeen`) && showPopup(popup);

      if (popup?.showTrigger === "leave") {
        document.body.addEventListener("mouseleave", handleListener);
      }

      if (popup && popup.showTrigger === "time") {
        popupTimeout = setTimeout(
          () => !getCookie(`${popup.id}-wasPopupSeen`) && showPopup(popup),
          popup.timeTriggerMs
        );
      }
      if (popup && popup.showTrigger === "new visitor") {
        if (!getCookie(`${popup.id}-wasPopupSeen`)) showPopup(popup);
      }

      return handleListener;
    });

    return () => {
      listeners.forEach((listener) =>
        document.body.removeEventListener("mouseleave", listener)
      );

      clearTimeout(popupTimeout);
    };
  }, [asPath]);

  const [isInteractive, setIsInteractive] = useState(true);
  const context = useMemo(
    () => ({
      findSlugByPageType,
      isInteractive,
      isMobile,
      setIsInteractive,
    }),
    [findSlugByPageType, isInteractive, isMobile, setIsInteractive]
  );

  return <AppContext.Provider value={context}>{children}</AppContext.Provider>;
}
